// src/pages/organiser/OrganiserDashboard.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../lib/AuthContext';
import { getOrganiserEvents, deleteEvent, createEvent } from '../../lib/db';
import { format } from 'date-fns';
import toast from 'react-hot-toast';
import { PageBack } from '../../components/shared/PageBack';

const OrganiserDashboard = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    const evs = await getOrganiserEvents(user.uid);
    setEvents(evs);
    setLoading(false);
  };

  useEffect(() => { load(); }, [user.uid]);

  const handleDelete = async (eventId, title) => {
    if (!window.confirm(`Delete "${title}"? This cannot be undone.`)) return;
    await deleteEvent(eventId);
    toast.success('Event deleted');
    load();
  };

  const handleDuplicate = async (event) => {
    const { id, interestCount, viewCount, createdAt, updatedAt, ...rest } = event;
    await createEvent({
      ...rest,
      title: `${event.title} (copy)`,
      status: 'published',
    });
    toast.success('Event duplicated');
    load();
  };

  const fmtDate = (date) => {
    if (!date) return '—';
    const d = date?.toDate ? date.toDate() : new Date(date);
    return format(d, 'd MMM yyyy');
  };

  const totalInterest = events.reduce((s, e) => s + (e.interestCount || 0), 0);
  const totalViews = events.reduce((s, e) => s + (e.viewCount || 0), 0);

  return (
    <div style={{ background: 'var(--cream)', minHeight: '100vh', paddingBottom: 40 }}>
      {/* Header */}
      <div style={{ padding: '52px 20px 20px' }}>
        <button onClick={() => navigate('/')} style={{ display: 'flex', alignItems: 'center', gap: 8, background: 'none', border: 'none', cursor: 'pointer', marginBottom: 16 }}>
          <div style={{ width: 34, height: 34, background: 'var(--white)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: 'var(--shadow-sm)', color: 'var(--sage-dark)', fontSize: 15 }}>←</div>
          <span style={{ fontSize: 13, color: 'var(--muted)', fontWeight: 600 }}>Home</span>
        </button>
        <div style={{ fontSize: 28, fontWeight: 800, color: 'var(--ink)', marginBottom: 4 }}>My Events</div>
        <div style={{ fontSize: 14, color: 'var(--muted)', fontWeight: 500 }}>Organiser dashboard</div>
      </div>

      {/* Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10, padding: '0 18px 20px' }}>
        {[['📅', events.length, 'Events'], ['❤️', totalInterest, 'Interested'], ['👁️', totalViews, 'Views']].map(([icon, val, lbl]) => (
          <div key={lbl} style={{ background: 'var(--white)', borderRadius: 16, padding: '14px 12px', textAlign: 'center', boxShadow: 'var(--shadow-sm)' }}>
            <div style={{ fontSize: 22 }}>{icon}</div>
            <div style={{ fontSize: 22, fontWeight: 800, color: 'var(--ink)', marginTop: 4 }}>{val}</div>
            <div style={{ fontSize: 11, color: 'var(--muted)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 1 }}>{lbl}</div>
          </div>
        ))}
      </div>

      {/* Post button */}
      <div style={{ padding: '0 18px 8px' }}>
        <button onClick={() => navigate('/post')} style={{ width: '100%', background: 'var(--sage-deep)', color: 'white', border: 'none', borderRadius: 16, padding: 16, fontSize: 15, fontWeight: 700, fontFamily: 'Nunito', cursor: 'pointer' }}>
          + Post a new event
        </button>
      </div>

      <div style={{ padding: '0 18px 8px' }}><button onClick={() => navigate('/organiser/bulk')} style={{ width: '100%', background: 'var(--white)', color: 'var(--sage-dark)', border: '1.5px solid #C8D9C9', borderRadius: 16, padding: 14, fontSize: 14, fontWeight: 700, fontFamily: 'Nunito', cursor: 'pointer' }}>✨ Add multiple events with AI</button></div>

      {/* Share with another organiser */}
      <div style={{ padding: '0 18px 16px' }}>
        <button onClick={() => {
          const townName = localStorage.getItem('gather-town-id') === 'loughrea' ? 'Loughrea' : 'our town';
          const msg = `Hey! I've been posting our events on Gather — it's a free community noticeboard for ${townName}. Takes 60 seconds to add an event and it reaches everyone in town. Worth putting your events up too: https://gatheryourcommunity.com`;
          window.open(`https://wa.me/?text=${encodeURIComponent(msg)}`, '_blank');
        }} style={{ width: '100%', background: '#EBF2EB', color: 'var(--sage-dark)', border: 'none', borderRadius: 16, padding: 14, fontSize: 14, fontWeight: 700, fontFamily: 'Nunito', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
          <span>📲</span> Invite another organiser via WhatsApp
        </button>
      </div>

      {/* Events list */}
      <div style={{ padding: '0 18px', display: 'flex', flexDirection: 'column', gap: 10 }}>
        <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1.2px', color: 'var(--muted)', marginBottom: 4 }}>Your events</div>
        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 32 }}><div className="spinner" /></div>
        ) : events.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-icon">📅</div>
            <div className="empty-state-title">No events yet</div>
            <div className="empty-state-body">Post your first event and it'll appear here.</div>
          </div>
        ) : events.map(event => (
          <div key={event.id} style={{ background: 'var(--white)', borderRadius: 16, padding: '14px 16px', boxShadow: 'var(--shadow-sm)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 6 }}>
              <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--ink)', flex: 1 }}>{event.title}</div>
              {event.repeat && event.repeat !== 'once' && (
                <span style={{ background: 'var(--sage-pale)', color: 'var(--sage-deep)', fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 100, marginLeft: 8, flexShrink: 0 }}>🔁 {event.repeat}</span>
              )}
            </div>
            <div style={{ fontSize: 12, color: 'var(--muted)', fontWeight: 500, marginBottom: 10 }}>
              {fmtDate(event.nextDate || event.date)} · {event.venue}
            </div>
            <div style={{ display: 'flex', gap: 12, marginBottom: 12 }}>
              <span style={{ fontSize: 12, color: 'var(--sage-deep)', fontWeight: 600 }}>❤️ {event.interestCount || 0} interested</span>
              <span style={{ fontSize: 12, color: 'var(--muted)', fontWeight: 500 }}>👁️ {event.viewCount || 0} views</span>
              {event.eventType === 'paid' && <span style={{ fontSize: 12, color: 'var(--warm-dark)', fontWeight: 600 }}>🎟 {event.eventCharge}</span>}
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={() => navigate(`/organiser/edit/${event.id}`)}
                style={{ flex: 1, background: 'var(--sage-pale)', color: 'var(--sage-dark)', border: 'none', borderRadius: 10, padding: '8px 12px', fontSize: 13, fontWeight: 700, fontFamily: 'Nunito', cursor: 'pointer' }}>
                Edit
              </button>
              <button onClick={() => handleDuplicate(event)}
                style={{ flex: 1, background: '#EBF2EB', color: 'var(--sage-dark)', border: 'none', borderRadius: 10, padding: '8px 12px', fontSize: 13, fontWeight: 700, fontFamily: 'Nunito', cursor: 'pointer' }}>
                Duplicate
              </button>
              <button onClick={() => handleDelete(event.id, event.title)}
                style={{ background: 'var(--error-pale)', color: 'var(--error)', border: 'none', borderRadius: 10, padding: '8px 12px', fontSize: 13, fontWeight: 700, fontFamily: 'Nunito', cursor: 'pointer' }}>
                Delete
              </button>
            </div>
          </div>
        ))}
      </div>
      <PageBack position="bottom" fallbackPath="/profile" />
    </div>
  );
};

export default OrganiserDashboard;
